import React, { useEffect, useRef, useState } from "react";
import {
    Box, CssBaseline, Breadcrumbs, Link, Typography, Stack, Button,
    Paper, Grid, TextField, CircularProgress, Divider, Snackbar, Alert,
    IconButton, CardMedia, MenuItem,
    Tooltip
} from "@mui/material";
import {
    NavigateNext, CloudUpload, Save, ArrowBack,
    Image as ImageIcon, Close, DeleteForever
} from '@mui/icons-material';
import { Form, Formik } from "formik";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../component/header/Header";

// Firebase
import { initializeApp } from "firebase/app";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const firebaseConfig = {
    apiKey: "AIzaSyBgoyz9VCXmUTVZLG32UCrSOiegRejv7sU",
    authDomain: "genius-milenia-education.firebaseapp.com",
    projectId: "genius-milenia-education",
    storageBucket: "genius-milenia-education.appspot.com",
    messagingSenderId: "103259384991",
    appId: "1:103259384991:web:9a66b97321936f7156c6aa",
};

const firebaseApp = initializeApp(firebaseConfig);
const storage = getStorage(firebaseApp);

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    accent: '#FFC107',
    accentDark: '#FFA000',
    filterBg: "#FFF9E6",
    textMain: '#5D4037',
};

const EditAnnouncementScreen = () => {
    const { id } = useParams();
    const navigation = useNavigate();
    const formikRef = useRef();

    const [loading, setLoading] = useState(true);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [announcementDetail, setAnnouncementDetail] = useState(null);
    const [file, setFile] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        getAnnouncementDetail();
        localStorage.setItem("current_tab", "Announcement");
    }, [id]);

    const getAnnouncementDetail = async () => {
        setLoading(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/announcement/${id}`);
            const resJson = await response.json();
            if (resJson.status === 1) {
                setAnnouncementDetail(resJson.data);
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to load data", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (event) => {
        if (event.target.files[0]) {
            setFile(event.target.files[0]);
        }
    };

    const handleSubmit = async (values) => {
        setLoadingSubmit(true);
        let finalUrl = values.imageUrl;

        try {
            // If a new file is selected, upload to Firebase
            if (file) {
                const storageRef = ref(storage, `announcements/${Date.now()}_${file.name}`);
                await uploadBytes(storageRef, file);
                finalUrl = await getDownloadURL(storageRef);
            }

            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/announcement/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...values,
                    imageUrl: finalUrl,
                })
            });

            const resJson = await response.json();
            if (resJson.status === 1) {
                setSnackbar({ open: true, message: "Announcement updated successfully!", severity: "success" });
                setTimeout(() => navigation("/Portal/Announcement"), 1500);
            } else {
                throw new Error(resJson.message);
            }
        } catch (error) {
            setSnackbar({ open: true, message: error.message || "Update failed", severity: "error" });
        } finally {
            setLoadingSubmit(false);
        }
    };

    const initialValues = {
        title: announcementDetail?.title || "",
        description: announcementDetail?.description || "",
        imageUrl: announcementDetail?.imageUrl || "",
        createdDate: announcementDetail?.createdDate || "",
        status: announcementDetail?.status || "A"
    };

    const validationSchema = yup.object({
        title: yup.string().required("Headline is required"),
        description: yup.string().required("Content body is required"),
    });

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
            <CircularProgress sx={{ color: colors.accent }} />
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

                {/* Navigation */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 1 }}>
                            <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                            <Link underline="hover" color="inherit" onClick={() => navigation("/Portal/Announcement")} sx={{ cursor: 'pointer' }}>Announcements</Link>
                            <Typography color="text.primary">Edit</Typography>
                        </Breadcrumbs>
                        <Header title="Edit Announcement" subtitle="Update current news and broadcast info" />
                    </Box>
                    <Button startIcon={<ArrowBack />} onClick={() => navigation(-1)} color="inherit">Back</Button>
                </Stack>

                <Formik
                    innerRef={formikRef}
                    enableReinitialize={true}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleBlur, handleChange, setFieldValue }) => (
                        <Form>
                            <Grid container spacing={4}>

                                {/* Image Section */}
                                <Grid item xs={12} md={5}>
                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Announcement Cover</Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            height: 350,
                                            borderRadius: 4,
                                            border: '2px dashed #DDD',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            bgcolor: '#FBFBFB'
                                        }}
                                    >
                                        {(file || values.imageUrl) ? (
                                            <>
                                                <CardMedia
                                                    component="img"
                                                    image={file ? URL.createObjectURL(file) : values.imageUrl}
                                                    sx={{ height: '100%', width: '100%', objectFit: 'cover' }}
                                                />
                                                <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 10, right: 10 }}>
                                                    <Tooltip title="Change Image">
                                                        <IconButton component="label" sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'white' } }}>
                                                            <CloudUpload fontSize="small" />
                                                            <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Remove Image">
                                                        <IconButton
                                                            onClick={() => { setFile(null); setFieldValue("imageUrl", ""); }}
                                                            sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: 'error.main', '&:hover': { bgcolor: 'white' } }}
                                                        >
                                                            <DeleteForever fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </>
                                        ) : (
                                            <Stack spacing={1} alignItems="center">
                                                <ImageIcon sx={{ fontSize: 60, color: '#CCC' }} />
                                                <Button variant="outlined" component="label" sx={{ borderRadius: 2, textTransform: 'none', mt: 2 }}>
                                                    Upload New Image
                                                    <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                                                </Button>
                                            </Stack>
                                        )}
                                    </Paper>
                                </Grid>

                                {/* Content Section */}
                                <Grid item xs={12} md={7}>
                                    <Paper elevation={0} sx={{ p: 3, bgcolor: colors.filterBg, borderRadius: 4 }}>
                                        <Stack spacing={3}>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>Announcement Title</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="title"
                                                    value={values.title}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.title && !!errors.title}
                                                    helperText={touched.title && errors.title}
                                                    sx={{ bgcolor: 'white' }}
                                                />
                                            </Box>

                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>Content Description</Typography>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={8}
                                                    name="description"
                                                    value={values.description}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.description && !!errors.description}
                                                    helperText={touched.description && errors.description}
                                                    sx={{ bgcolor: 'white' }}
                                                />
                                            </Box>

                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>Status</Typography>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    name="status"
                                                    value={values.status}
                                                    onChange={handleChange}
                                                    sx={{ bgcolor: 'white' }}
                                                >
                                                    <MenuItem value="A">Active (Visible)</MenuItem>
                                                    <MenuItem value="I">Inactive (Hidden)</MenuItem>
                                                </TextField>
                                            </Box>
                                        </Stack>
                                    </Paper>

                                    <Divider sx={{ my: 3 }} />

                                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                                        <Button
                                            variant="outlined"
                                            onClick={() => navigation(-1)}
                                            sx={{ borderRadius: 3, textTransform: 'none' }}
                                        >
                                            Discard
                                        </Button>
                                        <Button
                                            variant="contained"
                                            type="submit"
                                            disabled={loadingSubmit}
                                            startIcon={loadingSubmit ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                            sx={{
                                                bgcolor: colors.accent,
                                                color: 'black',
                                                px: 6,
                                                py: 1.5,
                                                borderRadius: 3,
                                                fontWeight: 'bold',
                                                '&:hover': { bgcolor: colors.accentDark }
                                            }}
                                        >
                                            {loadingSubmit ? "Saving Changes..." : "Update Announcement"}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            </Box>
        </Box>
    );
};

export default EditAnnouncementScreen;